import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "react",
  name: "React",
  nameEn: "React",
  seo: {
    title: "Reactを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "ReactはMeta社が開発したフロントエンドUIライブラリで、求人数No.1。ゆめさくエンジニアで実践的なReact開発スキルを身につけましょう。", // claims-ok 技術や職種の一般的な話
    url: "/columns/react-programming-school",
  },
  hero: {
    title: "Reactを学べる女性向けプログラミングスクール",
    englishTitle: "Learn React",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "React" },
  ],
  overview: {
    heading: "Reactとは？",
    paragraphs: [
      "Reactは、Meta（旧Facebook）が2013年に開発したJavaScriptのUIライブラリです。コンポーネントベースのアーキテクチャにより、再利用可能なUI部品を組み合わせてWebアプリケーションを構築できます。",
      "フロントエンドフレームワーク・ライブラリの中で圧倒的なシェアを持ち、国内外の求人数でも常にNo.1です。Instagram、Netflix、Airbnb、Uber、Slackなど、世界中の主要サービスがReactで構築されています。", // claims-ok 技術や職種の一般的な話
      "Next.jsやRemixなどのフレームワークと組み合わせることで、SSR（サーバーサイドレンダリング）やSSG（静的サイト生成）にも対応でき、SEOに強いWebアプリケーションも開発可能です。",
    ],
    useCases: [
      {
        label: "SPA（シングルページアプリ）開発",
        description:
          "ページ遷移なしで滑らかに動作するWebアプリケーションを構築できます。管理画面やダッシュボードなどに最適です。",
      },
      {
        label: "ECサイト・Webサービス",
        description:
          "ショッピングカート、リアルタイム検索、フィルタリングなど、インタラクティブなUI実装に威力を発揮します。",
      },
      {
        label: "モバイルアプリ開発",
        description:
          "React Nativeを使えば、Reactの知識でiOS・Androidアプリを同時に開発でき、スキルの応用範囲が広がります。",
      },
      {
        label: "コーポレートサイト・メディア",
        description:
          "Next.jsと組み合わせて、高速でSEOに強い企業サイトやメディアサイトを構築できます。",
      },
    ],
  },
  whyLearn: {
    heading: "Reactを学ぶべき3つの理由",
    items: [
      {
        title: "フロントエンド求人数No.1", // claims-ok 技術や職種の一般的な話
        description:
          "React関連の求人は国内外でフロントエンドフレームワークの中で最も多く、Vue.jsやAngularを大きく引き離しています。Reactを習得すれば、就職・転職時の選択肢が最大化されます。",
      },
      {
        title: "コンポーネント思考で保守性の高いコードが書ける",
        description:
          "UIを小さな部品（コンポーネント）に分割して開発するため、コードの再利用性と保守性が格段に向上します。この考え方はReact以外のフレームワークでも活きるため、エンジニアとしての基盤が鍛えられます。",
      },
      {
        title: "エコシステムが充実し、最新技術に触れられる",
        description:
          "Next.js、React Native、Redux、React Queryなど、巨大なエコシステムが形成されています。Web開発の最前線で使われる技術を学べるため、エンジニアとしての市場価値を高められます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでReactを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、HTML/CSS → JavaScript → Reactと段階的に進むカリキュラムで、確実にスキルを積み上げます。Reactの基本概念であるコンポーネント、State、Props、Hooksを実践的に学び、実際のWebアプリケーションを開発します。",
      "TypeScriptとの組み合わせや、Next.jsを使ったフルスタック開発まで踏み込むため、現場で即戦力となるスキルセットを習得できます。",
    ],
    topics: [
      "Reactの基本概念（JSX・コンポーネント・Props）",
      "State管理・useStateとuseEffect",
      "カスタムHooksの設計・実装",
      "React Router によるページ遷移",
      "API連携・データフェッチング",
      "実践プロジェクト：React + TypeScriptでWebアプリ開発",
    ],
  },
  careerPaths: {
    heading: "Reactを活かせるキャリア",
    items: [
      {
        title: "フロントエンドエンジニア",
        description:
          "React + TypeScriptでモダンなWebアプリケーションのUI構築を担当します。UI/UXへのこだわりとコンポーネント設計力が求められる職種です。",
        salary: "500〜800万円",
      },
      {
        title: "フルスタックエンジニア",
        description:
          "Next.jsなどのフレームワークを活用して、フロントエンドからバックエンドまで一気通貫で開発できるエンジニア。スタートアップで特に需要が高いポジションです。",
        salary: "550〜950万円",
      },
      {
        title: "React Nativeモバイルエンジニア",
        description:
          "Reactの知識を活かして、iOS・Androidアプリを同時に開発します。Web + モバイルの両方に対応できるスキルは市場価値が非常に高いです。",
        salary: "500〜850万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "8〜14ヶ月（HTML/CSS + JS + React）",
        luagate: "4〜5ヶ月（段階的カリキュラムで効率化）",
      },
      {
        label: "挫折率",
        selfStudy: "State管理や非同期処理で挫折しやすい",
        luagate: "概念を図解＋実践で段階的に理解",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "ToDoアプリ止まりで終わりがち",
        luagate: "実務レベルのWebアプリを企画・設計から開発",
      },
      {
        label: "質問対応",
        selfStudy: "コンポーネント設計の良し悪しがわからない",
        luagate: "現役エンジニアがコードレビューで設計力を指導",
      },
      {
        label: "就職サポート",
        selfStudy: "チュートリアルコピーのポートフォリオでは差別化困難",
        luagate: "オリジナルReactアプリで企業にアピール",
      },
    ],
  },
  faq: [
    {
      question: "ReactとVue.jsのどちらを学ぶべきですか？",
      answer:
        "求人数と将来性で選ぶなら、Reactをおすすめします。国内外ともにReactの求人が最も多く、Meta・Vercelなどの大企業が開発を主導しているため、長期的なサポートも安心です。",
    },
    {
      question: "Reactを学ぶ前にJavaScriptの知識は必要ですか？",
      answer:
        "はい、ReactはJavaScriptのライブラリなので、JavaScriptの基礎（変数、関数、配列、オブジェクト、非同期処理）を先に理解しておく必要があります。ゆめさくエンジニアではJavaScriptからReactへ段階的に学べます。",
    },
    {
      question: "Reactの学習は難しいですか？",
      answer:
        "コンポーネントやStateの概念は最初は戸惑いますが、HTML/CSS・JavaScriptの基礎があれば十分に理解できます。ゆめさくエンジニアでは実際にアプリを作りながら学ぶため、概念の理解が自然と深まります。",
    },
    {
      question: "ReactとNext.jsの違いは何ですか？",
      answer:
        "Reactはライブラリで、UIコンポーネントの構築に特化しています。Next.jsはReactベースのフレームワークで、ルーティング・SSR・API開発など、アプリ開発に必要な機能が統合されています。実務ではNext.jsを使うケースが増えています。",
    },
  ],
  relatedLanguages: [
    { slug: "javascript", name: "JavaScript" },
    { slug: "typescript", name: "TypeScript" },
    { slug: "html-css", name: "HTML/CSS" },
    { slug: "python", name: "Python" },
  ],
};
